// Passing Tendencies widget (Players canvas, `player-passing-tendencies`) — old
// Catalyst's "Tendencies" detail tile: the final-third direction mix plus a grid
// of style descriptors (pass length, long balls, average location, risk). Each
// descriptor shows the position-group average alongside. Positional basis;
// shares the peer query.

import { useMemo } from "react";
import { useMetricCategories } from "../data/stats.js";
import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import { num, stackedShares } from "./passing.js";
import { usePassingComparison } from "./usePassingComparison.js";
import PassingScope from "./PassingScope.js";

interface Seg {
  key: string;
  label: string;
  color: string;
}
const F3_DIRECTION: Seg[] = [
  { key: "op_f3_forward_pass_perc", label: "Forward", color: "#f9a8d4" },
  { key: "op_f3_sideways_pass_perc", label: "Sideways", color: "#fde047" },
  { key: "op_f3_backward_pass_perc", label: "Backward", color: "#60a5fa" },
];
const STAT_KEYS = [
  "pass_length",
  "long_ball_perc",
  "average_x_pass",
  "pass_into_pressure_perc",
  "pass_into_danger_perc",
  "left_foot_perc",
  "op_f3_passes_90",
];
const ALL_KEYS = [...F3_DIRECTION.map((s) => s.key), ...STAT_KEYS];

export default function PassingTendenciesWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  const cmp = usePassingComparison(playerId, competitionId, seasonId, ALL_KEYS);
  const cats = useMetricCategories(true);

  const info = useMemo(() => {
    const map: Record<string, { shortName: string; outputType?: string | null }> = {};
    for (const c of cats.data ?? [])
      for (const s of c.subcategories)
        for (const m of s.playerMetrics) map[m.key] = { shortName: m.shortName, outputType: m.outputType };
    return map;
  }, [cats.data]);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null)
    return <Msg>Pick a competition and season above.</Msg>;
  if (cmp.isPending || cats.isPending) return <Msg>Loading tendencies…</Msg>;
  if (cmp.isError) return <Msg danger>Couldn't load tendencies.</Msg>;
  if (!cmp.playerRow) return <Msg>No passing data for this player in this season.</Msg>;

  const row = cmp.playerRow;
  const avg = cmp.peerAvg;
  const f3 = stackedShares(row, F3_DIRECTION.map((s) => s.key));
  const f3ByKey = new Map(f3.map((s) => [s.key, s]));

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 14, overflowY: "auto" }}>
      <PassingScope cmp={cmp} />

      {f3.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ color: theme.inkFaint, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Final-third direction mix
          </div>
          <div style={{ display: "flex", height: 20, borderRadius: 6, overflow: "hidden" }}>
            {F3_DIRECTION.map((s) => (
              <div key={s.key} style={{ width: `${f3ByKey.get(s.key)?.widthPct ?? 0}%`, background: s.color }} title={s.label} />
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {F3_DIRECTION.map((s) => (
              <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: theme.inkMuted }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: s.color, display: "inline-block" }} />
                {s.label}{" "}
                <span style={{ color: theme.ink, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                  {((f3ByKey.get(s.key)?.value ?? 0) * 100).toFixed(1)}%
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
        {STAT_KEYS.map((key) => {
          const ot = info[key]?.outputType;
          return (
            <StatField
              key={key}
              label={info[key]?.shortName ?? key}
              value={fmtValue(num(row, key), ot)}
              sub={num(avg, key) === null ? undefined : `Pos avg ${fmtValue(num(avg, key), ot)}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function StatField({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <span style={{ color: theme.inkMuted, fontSize: 11 }}>{label}</span>
      <span style={{ color: theme.ink, fontSize: 15, fontWeight: 600, fontVariantNumeric: "tabular-nums", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
        {value}
      </span>
      {sub && <span style={{ color: theme.inkFaint, fontSize: 11 }}>{sub}</span>}
    </div>
  );
}

/** Format a value using its catalogue outputType (percentage → ×100). */
function fmtValue(v: number | null, outputType?: string | null): string {
  if (v === null) return "—";
  if (outputType === "percentage") return `${(v * 100).toFixed(1)}%`;
  if (outputType === "integer") return v.toFixed(0);
  return v.toFixed(2);
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
